import { format, isValid, parseISO } from 'date-fns';
import isEqual from 'lodash.isequal';
import React, { useCallback, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { NumericFormat } from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { MODAL_KEYS } from '_constants/modals';
import { REPORT_KEYS } from '_constants/reports';
import { theme } from '_constants/theme';
import { SOURCE, TRANSACTION_TYPES } from '_constants/transactions';
import { luminance } from '_helpers/theme';
import { usePrevious } from '_hooks';
import { showModal } from '_store/modals/slice';
import { selectActiveMainProductId, selectActiveMainProductSource } from '_store/order/selector';
import { selectData, selectLoading, selectParams } from '_store/reports/selector';
import { getBankTransactions, refreshParams } from '_store/reports/slice';
import { selectCustomerId } from '_store/user/selector';

import Button from 'components/atomic/Button';
import Icon from 'components/atomic/Icon';
import Paginate from 'components/atomic/Paginate';
import Table from 'components/atomic/Table';
import Card from 'components/atomic/layouts/Card';
import Text from 'components/atomic/layouts/Text';

import Filters from './Filters';
import TransactionsList from './TransactionsList';

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spaces.small}px;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
`;

const StyledBody = styled.div``;

const StyledEmptyDataContainer = styled.div`
  padding-top: ${theme.spaces.medium}px;
`;

const StyledPaginateContainer = styled.div`
  padding-top: ${theme.spaces.large}px;
`;

const StyledUpdateIconContainer = styled.span`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  cursor: pointer;

  &:hover {
    svg {
      path {
        stroke: ${theme.colors.indigo.primary};
      }
    }
  }
`;

const StyledDeleteIconContainer = styled.span`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  cursor: pointer;

  &:hover {
    svg {
      path {
        stroke: ${theme.colors.red.primary};
      }
    }
  }
`;

const getColumns = ({ source, handleUpdate, handleDelete }) => {
  const columns = [
    {
      dataIndex: 'typeIcon',
      title: null,
      width: '5%',
      minWidth: '30px',
      render: (row) => (
        <Icon
          alias={row?.type === TRANSACTION_TYPES.spend ? ICON_ALIASES.cardSend : ICON_ALIASES.cardReceive}
          color={
            row?.type === TRANSACTION_TYPES.spend
              ? luminance(theme.colors.red.primary, 0.4)
              : luminance(theme.colors.green.primary, 0.1)
          }
        />
      ),
    },
    {
      dataIndex: 'type',
      title: 'Type',
      width: '10%',
      render: (row) => (
        <Text
          size={theme.fontSizes.small}
          weight={400}
          color={
            row?.type === TRANSACTION_TYPES.spend
              ? luminance(theme.colors.red.primary, 0.4)
              : luminance(theme.colors.green.primary, 0.1)
          }
        >
          {row?.type}
        </Text>
      ),
    },
    {
      dataIndex: 'date',
      title: 'Date',
      width: '10%',
      render: (row) => {
        const parsedDate = parseISO(row?.date);

        return (
          <Text
            size={theme.fontSizes.small}
            weight={400}
            color={
              row?.type === TRANSACTION_TYPES.spend
                ? luminance(theme.colors.red.primary, 0.4)
                : luminance(theme.colors.green.primary, 0.1)
            }
          >
            {isValid(parsedDate) ? format(parsedDate, 'MM/dd/yyyy') : row?.date}
          </Text>
        );
      },
    },
    {
      dataIndex: 'accountName',
      title: 'Category',
      width: '20%',
      render: (row) => {
        return (
          <Text
            size={theme.fontSizes.small}
            weight={400}
            color={
              row?.type === TRANSACTION_TYPES.spend
                ? luminance(theme.colors.red.primary, 0.4)
                : luminance(theme.colors.green.primary, 0.1)
            }
          >
            {row?.accountName || 'N/A'}
          </Text>
        );
      },
    },
    {
      dataIndex: 'contactName',
      title: 'Contact Name',
      width: [SOURCE.customer, SOURCE.plaid].includes(source) ? '12%' : '15%',
      render: (row) => {
        return (
          <Text
            size={theme.fontSizes.small}
            weight={400}
            color={
              row?.type === TRANSACTION_TYPES.spend
                ? luminance(theme.colors.red.primary, 0.4)
                : luminance(theme.colors.green.primary, 0.1)
            }
          >
            {row?.contactName || 'N/A'}
          </Text>
        );
      },
    },
    {
      dataIndex: 'total',
      title: 'Total',
      width: '12%',
      right: true,
      render: (row) => (
        <Text
          size={theme.fontSizes.small}
          weight={400}
          color={
            row?.type === TRANSACTION_TYPES.spend
              ? luminance(theme.colors.red.primary, 0.4)
              : luminance(theme.colors.green.primary, 0.1)
          }
          ignoreSpan
        >
          <NumericFormat
            displayType="text"
            prefix="$"
            value={row?.total}
            thousandSeparator=","
            decimalScale={2}
            fixedDecimalScale
          />
        </Text>
      ),
    },
    {
      dataIndex: 'span',
      width: '3%',
    },
    {
      dataIndex: 'currencyCode',
      title: 'Currency',
      width: '10%',
      render: (row) => (
        <Text
          size={theme.fontSizes.small}
          weight={400}
          color={
            row?.type === TRANSACTION_TYPES.spend
              ? luminance(theme.colors.red.primary, 0.4)
              : luminance(theme.colors.green.primary, 0.1)
          }
        >
          {row?.currencyCode}
        </Text>
      ),
    },
    {
      dataIndex: 'reference',
      title: 'Reference',
      width: [SOURCE.customer, SOURCE.plaid].includes(source) ? '12%' : '15%',
      render: (row) => (
        <Text
          size={theme.fontSizes.small}
          weight={400}
          color={
            row?.type === TRANSACTION_TYPES.spend
              ? luminance(theme.colors.red.primary, 0.4)
              : luminance(theme.colors.green.primary, 0.1)
          }
        >
          {row?.reference}
        </Text>
      ),
    },
  ];

  if ([SOURCE.customer, SOURCE.plaid].includes(source)) {
    return [
      ...columns,
      {
        dataIndex: 'update',
        title: null,
        width: '3%',
        render: (row) => (
          <StyledUpdateIconContainer onClick={handleUpdate(row)}>
            <Icon alias={ICON_ALIASES.documentEdit} color={theme.colors.gray.secondary} height={18} width={18} />
          </StyledUpdateIconContainer>
        ),
      },
      {
        dataIndex: 'delete',
        title: null,
        width: '3%',
        render: (row) => (
          <StyledDeleteIconContainer onClick={handleDelete(row)}>
            <Icon alias={ICON_ALIASES.trash} color={theme.colors.gray.secondary} height={18} width={18} />
          </StyledDeleteIconContainer>
        ),
      },
    ];
  }

  return columns;
};

const TransactionsView = () => {
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const productId = useSelector(selectActiveMainProductId);
  const prevProductId = usePrevious(productId);
  const bankTransactionsReportParams = useSelector(selectParams(REPORT_KEYS.bankTransactions));
  const prevBankTransactionsReportParams = usePrevious(bankTransactionsReportParams);
  const source = useSelector(selectActiveMainProductSource);
  const data = useSelector(selectData(REPORT_KEYS.bankTransactions));
  const loading = useSelector(selectLoading(REPORT_KEYS.bankTransactions));

  const handlePageChange = useCallback(
    (page) => {
      dispatch(refreshParams({ [REPORT_KEYS.bankTransactions]: { page } }));
      dispatch(getBankTransactions({ customerId, productId }));
    },
    [customerId, productId]
  );

  const handleAdd = () => {
    dispatch(showModal({ key: MODAL_KEYS.transactionAdd }));
  };

  const handleUpdate = (data) => () => {
    dispatch(showModal({ key: MODAL_KEYS.transactionUpdate, data }));
  };

  const handleDelete = (data) => () => {
    dispatch(showModal({ key: MODAL_KEYS.transactionDelete, data }));
  };

  useEffect(() => {
    if (customerId && productId && !isEqual(prevBankTransactionsReportParams, bankTransactionsReportParams)) {
      dispatch(getBankTransactions({ customerId, productId }));
    }
  }, [customerId, productId, prevBankTransactionsReportParams, bankTransactionsReportParams]);

  useEffect(() => {
    if (customerId && productId && bankTransactionsReportParams && prevProductId !== productId) {
      dispatch(getBankTransactions({ customerId, productId }));
    }
  }, [customerId, prevProductId, productId, bankTransactionsReportParams]);

  return (
    <Card>
      <StyledHeader>
        <StyledTitleContainer>
          <Text size={theme.fontSizes.medium} weight={600}>
            Identify the following transactions
          </Text>
        </StyledTitleContainer>
        {[SOURCE.customer, SOURCE.plaid].includes(source) && (
          <div>
            <Button outlined disabled={!productId} color={theme.colors.indigo.primary} onClick={handleAdd}>
              + Add Transaction
            </Button>
          </div>
        )}
      </StyledHeader>
      <Filters />
      {!isMobile && (
        <StyledBody>
          <Table
            columns={getColumns({ source, handleUpdate, handleDelete })}
            rows={data?.elements}
            loading={loading}
            skeletonRowsNum={data?.maxPerPage}
          />
          {!data?.totalPages && (
            <StyledEmptyDataContainer>
              <Text size={theme.fontSizes.small} weight={400} color={theme.colors.gray.primary} centered>
                No data available
              </Text>
            </StyledEmptyDataContainer>
          )}
          <StyledPaginateContainer>
            <Paginate
              pageRangeDisplayed={3}
              pageCount={data?.totalPages}
              currentPage={data?.page}
              onPageChange={handlePageChange}
            />
          </StyledPaginateContainer>
        </StyledBody>
      )}
      {isMobile && (
        <StyledBody>
          <TransactionsList source={source} items={data?.elements} onUpdate={handleUpdate} onDelete={handleDelete} />
          {!data?.totalPages && (
            <StyledEmptyDataContainer>
              <Text size={theme.fontSizes.small} weight={400} color={theme.colors.gray.primary} centered>
                No data available
              </Text>
            </StyledEmptyDataContainer>
          )}
          <StyledPaginateContainer>
            <Paginate
              pageRangeDisplayed={0}
              pageCount={data?.totalPages}
              currentPage={data?.page}
              onPageChange={handlePageChange}
            />
          </StyledPaginateContainer>
        </StyledBody>
      )}
    </Card>
  );
};

export default TransactionsView;
